import { useEffect, useState } from "react"

import { isTextClamped } from "@productlane/lib/src/text-clamp"

/**
 * Whether a line-clamped element is hiding lines, re-measured whenever it
 * resizes. A one-off measurement goes stale as soon as the column narrows, and
 * the "Show more" button that depends on it disappears while text stays cut.
 *
 * Pass the node from a callback ref so the first measurement runs on mount.
 * While `enabled` is false the last verdict stands, so a control that expanded
 * the text keeps rendering.
 */
export function useTextClamped(
	element: HTMLElement | null,
	enabled: boolean,
	content?: string,
): boolean {
	const [clamped, setClamped] = useState(false)

	useEffect(() => {
		if (!element || !enabled) return

		const measure = () => setClamped(isTextClamped(element))
		measure()

		if (typeof ResizeObserver === "undefined") return
		const observer = new ResizeObserver(measure)
		observer.observe(element)
		return () => observer.disconnect()
	}, [element, enabled, content])

	return clamped
}
